"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArrayValueField = exports.Field = void 0;
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const store_1 = require("../../exports/store");
const field_types_1 = require("../../types/field_types");
const view_types_1 = require("../../types/view_types");
const index_1 = require("../index");
const stat_1 = require("./stat");
const validate_schema_1 = require("./validate_schema");
// China sensitive string comparison `collators` constructor.
const zhIntlCollator = typeof Intl !== 'undefined' ? new Intl.Collator('zh-CN') : undefined;
/**
 * The business class should not become a complex container, it is better to be just a pipeline of data flow
 * Here we use the form of classes to build business calculation methods,
 * but do not use new to initialize directly, but only bind the data that needs to be processed to the instance, and leave after use.
 * Suppose we want to call cellValueToString
 *
 * Example:
 * const field: ITextField = // Assuming there is a column attribute data for a text column;
 * const value = Field.bindContext(field, state).cellValueToString();
 *
 * As you can see, every time you use the methods on the Field class,
 * you only need bindContext for data binding, and then call the calculation method to get the desired return value.
 * No need to hold a concrete Field instance. This is very useful when we use it with redux immutable data.
 * Because immutable data is managed by reducer, it may be updated at any time, and we cannot hold data in our method class,
 * Instead, get the latest data from the latest state every time you use it to participate in the calculation.
 */
class Field {
    constructor(field, state) {
        this.field = field;
        this.state = state;
    }
    /**
     * the meta information of API return fields
     *
     * @param dstId
     * @returns
     */
    getApiMeta(dstId) {
        const views = store_1.Selectors.getViewsList(this.state, dstId);
        const firstFieldId = views[0].columns[0].fieldId;
        const res = {
            id: this.field.id,
            name: this.field.name,
            type: (0, index_1.getFieldTypeString)(this.field.type),
            property: this.apiMetaProperty || undefined,
            editable: this.recordEditable(),
        };
        if (this.field.id === firstFieldId) {
            res.isPrimary = true;
        }
        if (this.field.desc) {
            res.desc = this.field.desc;
        }
        return res;
    }
    // not includes basic value type in Array
    get valueType() {
        if (this.basicValueType === field_types_1.BasicValueType.Array) {
            return this.innerBasicValueType;
        }
        return this.basicValueType;
    }
    // default String, Array type field override
    get innerBasicValueType() {
        return field_types_1.BasicValueType.String;
    }
    showFOperatorDesc(type) {
        return view_types_1.FOperatorDescMap[type];
    }
    /**
     * whether the field support grouping
     */
    get canGroup() {
        return true;
    }
    /**
     * whether the calc field error
     */
    get hasError() {
        return false;
    }
    /**
     * calc field exception info
     */
    get warnText() {
        return '';
    }
    /**
     * current field analytic bar options
     */
    get statTypeList() {
        return [
            stat_1.StatType.None,
            stat_1.StatType.CountAll,
            stat_1.StatType.Empty,
            stat_1.StatType.Filled,
            stat_1.StatType.Unique,
            stat_1.StatType.PercentEmpty,
            stat_1.StatType.PercentFilled,
            stat_1.StatType.PercentUnique,
        ];
    }
    /**
     * current operator types that fieldType support, and the order in the dropdown box
     * every heritage field must be implemented
     */
    get acceptQueryOperators() {
        return this.acceptFilterOperators;
    }
    /**
     * The value of each cell is calculated through the field record relationship in the snapshot.
     * But the calculation here refers to fields such as LookUp, RollUp, Formula, etc.
     * The calculation of the cell value depends on the value of other basic cells.
     * Such fields are called "calculated fields" and need to override this method.
     * @readonly
     * @memberof Field
     */
    get isComputed() {
        return false;
    }
    /**
     * judge the filter, which FieldType belongs to?
     *
     * @template T
     * @param {T} t
     * @param {IFilterCondition<FieldType>} v
     * @returns {v is Extract<IFilterCondition<T>, IFilterCondition<FieldType>>}
     * @memberof Field
     */
    static isFilterBelongFieldType(t, v) {
        return v.fieldType === t;
    }
    isEmptyOrNot(operator, cellValue) {
        switch (operator) {
            /**
             * The logic of isEmpty and isNotEmpty is basically common use
             */
            case view_types_1.FOperator.IsEmpty: {
                return cellValue == null;
            }
            case view_types_1.FOperator.IsNotEmpty: {
                return cellValue != null;
            }
            default: {
                throw new Error('compare operator type error');
            }
        }
    }
    /**
     * Whether the current field can be edited
     * Not all fields allow users to edit. If it is a calculated field, such as rollUp, lookup, formula fields, users are not allowed to edit
     * In addition, related fields are only allowed to be edited
     * if the user has the editing permission of the table associated with the current related field.
     */
    recordEditable(datasheetId, mirrorId) {
        return store_1.Selectors.getPermissions(this.state, datasheetId, this.field.id, mirrorId).cellEditable;
    }
    /**
     * Whether the properties of the current field are allowed to be edited
     * Here it is judged according to the permission factor, and the edit field is allowed to be true.
     */
    propertyEditable() {
        return store_1.Selectors.getPermissions(this.state, undefined, this.field.id).fieldPropertyEditable;
    }
    /**
     * compare two cell value whether equal
     * by default, use deep comparison,
     * complicated cell value need to override this method
     *
     * TODO: make the logic of `eq` and the logic of `compare` together, delete `eq`
     *
     */
    eq(cv1, cv2) {
        return (0, lodash_1.isEqual)(cv1, cv2);
    }
    /**
     * on this field, compare 2 cell value size, for sorting
     * by default, compare with string conversion, if not, please implement it yourself.
     * @orderInCellValueSensitive {boolean}
     * when grouping, the sort of cells are insensitive, grouping them together.
     * when sorting, the sort of cells are sensitive.
     * when testing, multiple-choices, members, relations keep same logic.
     * @returns {number} negative number => smaller than | 0 => equals | positive number => larger than
     */
    compare(cellValue1, cellValue2, orderInCellValueSensitive) {
        if (this.eq(cellValue1, cellValue2)) {
            return 0;
        }
        if (cellValue1 == null) {
            return -1;
        }
        if (cellValue2 == null) {
            return 1;
        }
        let str1 = this.cellValueToString(cellValue1, { orderInCellValueSensitive });
        let str2 = this.cellValueToString(cellValue2, { orderInCellValueSensitive });
        if (str1 === str2) {
            return 0;
        }
        if (str1 == null) {
            return -1;
        }
        if (str2 == null) {
            return 1;
        }
        str1 = str1.trim();
        str2 = str2.trim();
        // test pinyin sort
        return str1 === str2 ? 0 :
            zhIntlCollator ? zhIntlCollator.compare(str1, str2) : (str1.localeCompare(str2, 'zh-CN') > 0 ? 1 : -1);
    }
    /**
     *
     * beside the operator calc function  that check whether empty,
     * other functions need to implement themselves
     * when no inheritance is needed, the default is true, which means no filtering
     *
     */
    isMeetFilter(operator, cellValue, _) {
        switch (operator) {
            case view_types_1.FOperator.IsEmpty:
            case view_types_1.FOperator.IsNotEmpty: {
                return this.isEmptyOrNot(operator, cellValue);
            }
            default: {
                console.warn('Method should be overwrite!');
                return true;
            }
        }
    }
    stringInclude(str, searchStr) {
        return str.toLowerCase().includes(searchStr.trim().toLowerCase());
    }
    // used for copy-paste/type conversion before getting a new record, according to the
    // Data fills the properties in Field, such as options in single/multi
    enrichProperty(_stdVals) {
        return this.field.property;
    }
    /**
     * Whether to multi-select fields of value type.
     * For multiple selections, the collaborator field will return true.
     * NOTE: Fields that are guaranteed to return true, value is an array.
     */
    isMultiValueField() {
        return false;
    }
    /**
     * Returns the default value of the field attribute configuration when adding a record
     */
    defaultValue() {
        return null;
    }
    /**
     * @description convert the statistical parameters into Chinese/English
     */
    statType2text(type) {
        const result = stat_1.StatTranslate[type];
        return result;
    }
    /**
     * Get property (widget, robot, api)
     */
    get openFieldProperty() {
        return null;
    }
    /**
     * Check update field property
     * @returns {Joi.ValidationResult}
     */
    validateUpdateOpenProperty(_updateProperty, _effectOption) {
        return (0, validate_schema_1.joiErrorResult)(`${(0, index_1.getFieldTypeString)(this.field.type)} not support set property`);
    }
    /**
     * Return field information externally
     * @param dstId
     */
    getOpenField(dstId) {
        const views = store_1.Selectors.getViewsList(this.state, dstId);
        const firstFieldId = views[0].columns[0].fieldId;
        const { id, name, desc: description, type, required } = this.field;
        const res = {
            id,
            name,
            description,
            type: (0, index_1.getFieldTypeString)(type),
            property: this.openFieldProperty,
            editable: this.recordEditable(),
            required
        };
        if (this.field.id === firstFieldId) {
            res.isPrimary = true;
        }
        return res;
    }
    /**
     * Check the property when creating the field
     */
    validateAddOpenFieldProperty(addProperty) {
        return this.validateUpdateOpenProperty(addProperty);
    }
    /**
     * Check whether the delete field parameter is qualified
     * @param deleteFieldSchema delete field parameter
     */
    validateOpenDeleteField(deleteFieldSchema) {
        return joi_1.default.object({
            id: joi_1.default.string().required(),
            conversion: joi_1.default.boolean(),
        }).required().validate(deleteFieldSchema);
    }
    /**
     *
     * For the time being, all fields are created and updated the same,
     * except that there is an additional conversion when the associated field is updated, so the conversion at the time of update is used directly
     *
     * Add a new field to convert the incoming property into a structure that can be sent with cmd
     * Note: This step will not verify the incoming parameters, and the verification is passed by default
     * @param openFieldProperty
     */
    addOpenFieldPropertyTransformProperty(addOpenFieldProperty) {
        return this.updateOpenFieldPropertyTransformProperty(addOpenFieldProperty);
    }
    /**
     * Update the field properties and convert the incoming property into a structure that can be sent with cmd
     * Note: This step will not verify the incoming parameters, and the verification is passed by default
     * @param openFieldProperty
     */
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        return openFieldProperty;
    }
    /**
     * Converting internal filter structures to external data structures.
     * @param _value
     */
    filterValueToOpenFilterValue(_value) {
        throw new Error(`${(0, index_1.getFieldTypeString)(this.field.type)} not support filterValueToOpenFilterValue`);
    }
    /**
     * Converting external filter structures to internal data structures.
     * @param value
     */
    openFilterValueToFilterValue(_value) {
        throw new Error(`${(0, index_1.getFieldTypeString)(this.field.type)} not support filterValueToOpenFilterValue`);
    }
    /**
     * Verify that the incoming external filter data structure is secure.
     * @param value
     */
    validateOpenFilterValue(_value) {
        return (0, validate_schema_1.joiErrorResult)(`${(0, index_1.getFieldTypeString)(this.field.type)} not support validateOpenFilterValue`);
    }
}
exports.Field = Field;
class ArrayValueField extends Field {
    get acceptFilterOperators() {
        return ArrayValueField._acceptFilterOperators;
    }
}
exports.ArrayValueField = ArrayValueField;
// Who sets the value of the field need to specify the value type in the array
ArrayValueField._acceptFilterOperators = [
    view_types_1.FOperator.Is,
    view_types_1.FOperator.IsNot,
    view_types_1.FOperator.Contains,
    view_types_1.FOperator.DoesNotContain,
    view_types_1.FOperator.IsEmpty,
    view_types_1.FOperator.IsNotEmpty,
    view_types_1.FOperator.IsRepeat,
];
//# sourceMappingURL=field.js.map